/**
 * Battleship Game
 * Naval combat game with ship placement and targeting
 */

import React, { useState } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import useGameAI from './hooks/useGameAI';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Battleship = ({ onHome }) => {
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('battleship_score', 0);
  const [highScore, setHighScore] = useLocalStorage('battleship_highScore', 0);
  const { getAIMove } = useGameAI();
  const [gameMode, setGameMode] = useState(null); // 'human' or 'computer'
  const [difficulty, setDifficulty] = useState(null); // 'easy', 'medium', 'hard'
  const [gamePhase2, setGamePhase2] = useState('menu'); // menu, difficulty, playing, gameover

  const GRID_SIZE = 10;
  const GRID_CELLS = GRID_SIZE * GRID_SIZE;

  const shipSizes = [5, 4, 3, 3, 2];
  const [playerBoard, setPlayerBoard] = useState(Array(GRID_CELLS).fill(0));
  const [computerBoard, setComputerBoard] = useState(Array(GRID_CELLS).fill(0));
  const [playerShots, setPlayerShots] = useState(Array(GRID_CELLS).fill(0)); // 0=not shot, 1=miss, 2=hit
  const [computerShots, setComputerShots] = useState(Array(GRID_CELLS).fill(0));
  const [playerShips, setPlayerShips] = useState([]);
  const [computerShips, setComputerShips] = useState([]);
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [shipsPlaced, setShipsPlaced] = useState(false);
  const [gamePhase, setGamePhase] = useState('placement'); // placement, playing, gameover
  const [playerHits, setPlayerHits] = useState(0);
  const [computerHits, setComputerHits] = useState(0);
  const [message, setMessage] = useState('Place your ships');

  const generateShips = () => {
    const ships = [];
    const placed = Array(GRID_CELLS).fill(false);

    for (let size of shipSizes) {
      let placed_ = false;
      while (!placed_) {
        const isHorizontal = Math.random() > 0.5;
        const row = Math.floor(Math.random() * GRID_SIZE);
        const col = Math.floor(Math.random() * (GRID_SIZE - size + 1));
        const startIdx = isHorizontal ? row * GRID_SIZE + col : row * GRID_SIZE + col;

        let canPlace = true;
        const cells = [];
        for (let i = 0; i < size; i++) {
          const idx = isHorizontal ? startIdx + i : startIdx + i * GRID_SIZE;
          if (idx >= GRID_CELLS || placed[idx]) {
            canPlace = false;
            break;
          }
          cells.push(idx);
        }

        if (canPlace) {
          cells.forEach((idx) => (placed[idx] = true));
          ships.push(cells);
          placed_ = true;
        }
      }
    }
    return ships;
  };

  const placePlayerShip = (index, size) => {
    if (playerShips.length >= shipSizes.length) return;

    const occupied = playerShips.flat();
    const isHorizontal = true;
    const row = Math.floor(index / GRID_SIZE);
    const col = index % GRID_SIZE;

    if (col + size > GRID_SIZE) return;

    const cells = [];
    for (let i = 0; i < size; i++) {
      const cellIdx = row * GRID_SIZE + col + i;
      if (occupied.includes(cellIdx)) return;
      cells.push(cellIdx);
    }

    setPlayerShips([...playerShips, cells]);
  };

  const startGame = () => {
    const compShips = generateShips();
    setComputerShips(compShips);

    const compBoard = Array(GRID_CELLS).fill(0);
    compShips.forEach((ship) => {
      ship.forEach((idx) => {
        compBoard[idx] = 1;
      });
    });
    setComputerBoard(compBoard);

    setGamePhase('playing');
    setIsPlayerTurn(true);
    setPlayerHits(0);
    setComputerHits(0);
    setScore(0);
    setMessage('Your turn - Click to fire!');
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (playerHits > highScore) setHighScore(playerHits);
    setGameState(GAME_CONFIG.states.MENU);
  };

  const fireShot = (index) => {
    if (!isPlayerTurn || gamePhase !== 'playing') return;
    if (playerShots[index] !== 0) return;

    const newPlayerShots = [...playerShots];
    const hit = computerBoard[index] === 1;
    newPlayerShots[index] = hit ? 2 : 1;
    setPlayerShots(newPlayerShots);

    if (hit) {
      setPlayerHits((prev) => prev + 1);
      setScore((prev) => prev + 50);

      // Check if ship sunk
      const sunkShip = computerShips.find(
        (ship) =>
          ship.includes(index) &&
          ship.every((cellIdx) => newPlayerShots[cellIdx] === 2)
      );
      if (sunkShip) {
        setScore((prev) => prev + 100);
      }

      // Check if won
      if (playerHits + 1 === shipSizes.reduce((a, b) => a + b, 0)) {
        setGamePhase('gameover');
        setMessage('You Won!');
        return;
      }
    }

    setIsPlayerTurn(false);
    setMessage('Computer is thinking...');

    setTimeout(() => {
      computerTurn(newPlayerShots);
    }, 1000);
  };

  const computerTurn = (playerShotsState) => {
    // Get all available moves (cells that haven't been shot)
    const possibleMoves = [];
    for (let i = 0; i < GRID_CELLS; i++) {
      if (computerShots[i] === 0) {
        possibleMoves.push(i);
      }
    }

    if (possibleMoves.length === 0) return;

    // Scoring function for Battleship strategy
    const scoreFunc = (moveIndex) => {
      let score = 0;
      const row = Math.floor(moveIndex / GRID_SIZE);
      const col = moveIndex % GRID_SIZE;

      // Check if there's a hit nearby (medium/hard strategy)
      if (difficulty !== 'easy') {
        // Check adjacent cells for hits
        const adjacent = [
          moveIndex - GRID_SIZE, // up
          moveIndex + GRID_SIZE, // down
          moveIndex - 1, // left
          moveIndex + 1, // right
        ];

        adjacent.forEach((adjIndex) => {
          if (adjIndex >= 0 && adjIndex < GRID_CELLS && computerShots[adjIndex] === 2) {
            score += 20; // High priority: next to a hit
          }
        });
      }

      // Chess-like pattern: space out shots (medium/hard)
      if (difficulty !== 'easy') {
        if ((row + col) % 2 === 0) {
          score += 3; // Prefer every other square (smarter pattern)
        }
      }

      return score;
    };

    // Get AI move based on difficulty
    const selectedIndex = getAIMove(difficulty, possibleMoves, scoreFunc);
    const index = selectedIndex !== undefined ? selectedIndex : possibleMoves[0];

    const newComputerShots = [...computerShots];
    const hit = playerBoard[index] === 1;
    newComputerShots[index] = hit ? 2 : 1;
    setComputerShots(newComputerShots);

    if (hit) {
      setComputerHits((prev) => prev + 1);

      // Check if won
      if (computerHits + 1 === shipSizes.reduce((a, b) => a + b, 0)) {
        setGamePhase('gameover');
        setMessage('Computer Won!');
        return;
      }
    }

    setIsPlayerTurn(true);
    setMessage(hit ? 'Computer hit! Your turn.' : 'Computer missed! Your turn.');
  };

  const renderBoard = (shots, isPlayerBoard = false) => {
    return Array(GRID_CELLS)
      .fill(0)
      .map((_, idx) => {
        const shot = shots[idx];
        let bgColor = '#667eea';
        let content = '';

        if (shot === 1) {
          bgColor = '#999'; // Miss
          content = '○';
        } else if (shot === 2) {
          bgColor = '#ff4444'; // Hit
          content = '💥';
        } else if (isPlayerBoard && playerShips.flat().includes(idx)) {
          bgColor = '#4444ff'; // Ship
        }

        return (
          <div
            key={idx}
            onClick={() => !isPlayerBoard && fireShot(idx)}
            style={{
              width: '30px',
              height: '30px',
              backgroundColor: bgColor,
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: !isPlayerBoard && gamePhase === 'playing' ? 'pointer' : 'default',
              fontSize: '0.8rem',
              color: '#fff',
              fontWeight: 'bold',
            }}
          >
            {content}
          </div>
        );
      });
  };

  return (
    <GameLayout
      title="Battleship ⚓"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Game Mode Selection */}
        {gameState === GAME_CONFIG.states.MENU && gamePhase2 === 'menu' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Battleship ⚓</h2>
            <p style={styles.menuDescription}>
              Sink all enemy ships before they sink yours!
            </p>
            <div style={styles.modeButtonsContainer}>
              <button
                onClick={() => {
                  setGameMode('human');
                  startGame();
                }}
                className="btn btn-primary btn-lg"
              >
                👥 Human vs Human
              </button>
              <button
                onClick={() => {
                  setGameMode('computer');
                  setGamePhase2('difficulty');
                }}
                className="btn btn-success btn-lg"
              >
                🤖 Human vs Computer
              </button>
            </div>
          </div>
        )}

        {/* Difficulty Selection */}
        {gameState === GAME_CONFIG.states.MENU && gamePhase2 === 'difficulty' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Select Difficulty</h2>
            <div style={styles.difficultyButtonsContainer}>
              <button
                onClick={() => {
                  setDifficulty('easy');
                  startGame();
                }}
                className="btn btn-primary"
              >
                🟢 Easy
              </button>
              <button
                onClick={() => {
                  setDifficulty('medium');
                  startGame();
                }}
                className="btn btn-warning"
              >
                🟡 Medium
              </button>
              <button
                onClick={() => {
                  setDifficulty('hard');
                  startGame();
                }}
                className="btn btn-danger"
              >
                🔴 Hard
              </button>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && gamePhase === 'playing' && (
          <div style={styles.gameContainer}>
            <div style={{ color: GAME_CONFIG.colors.light, marginBottom: '1rem', textAlign: 'center' }}>
              {message}
            </div>

            <div style={styles.boardsContainer}>
              <div style={styles.boardSection}>
                <h4 style={{ color: GAME_CONFIG.colors.light }}>Your Board</h4>
                <div style={styles.board}>{renderBoard(computerShots, false)}</div>
                <div style={{ color: GAME_CONFIG.colors.accent2, marginTop: '0.5rem' }}>
                  Hits: {playerHits}
                </div>
              </div>

              <div style={styles.boardSection}>
                <h4 style={{ color: GAME_CONFIG.colors.light }}>Enemy Board</h4>
                <div style={styles.board}>{renderBoard(playerShots, true)}</div>
                <div style={{ color: GAME_CONFIG.colors.accent2, marginTop: '0.5rem' }}>
                  Enemy Hits: {computerHits}
                </div>
              </div>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && gamePhase === 'gameover' && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>{message}</h1>
            <ScoreDisplay score={score} stats={{ 'Ships Sunk': playerHits }} />
            <button
              onClick={() => {
                setGameState(GAME_CONFIG.states.MENU);
                setGamePhase('placement');
                setPlayerShips([]);
                setPlayerShots(Array(GRID_CELLS).fill(0));
                setComputerShots(Array(GRID_CELLS).fill(0));
              }}
              className="btn btn-primary mt-3"
              style={{ width: '100%' }}
            >
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '2rem',
  },
  modeButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  difficultyButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  gameContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
  },
  boardsContainer: {
    display: 'flex',
    gap: '3rem',
    justifyContent: 'center',
  },
  boardSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  board: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 30px)',
    gap: '0',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.accent,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default Battleship;
